import { c as _c } from "react/compiler-runtime";
import type { CountryRecord } from '@onefootprint/core';
import { COUNTRIES } from '@onefootprint/core';
import { IcoTrash16 } from '@onefootprint/icons';
import type { Iso3166TwoDigitCountryCode } from '@onefootprint/request-types';
import type { CountrySpecificDocumentMapping, IdDocKind } from '@onefootprint/request-types/dashboard';
import { Button, Divider, LinkButton, Select } from '@onefootprint/ui';
import { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toDocumentTypesAndCountries } from '../../../../id-doc-kinds';
import type { FormData } from '../../../id-docs.types';
import CheckboxChip from './components/checkbox-chip';
import useIdDocOptions from './hooks/use-id-doc-options';
import useSelectedDocuments from './hooks/use-selected-documents';
type PickerProps = {
  editingCountryDocMap?: CountrySpecificDocumentMapping;
  onSave: (country: Iso3166TwoDigitCountryCode, docs: IdDocKind[]) => void;
  onRemove: (country: Iso3166TwoDigitCountryCode) => void;
  onCancel: () => void;
};
const Picker = t0 => {
  const $ = _c(77);
  const {
    editingCountryDocMap,
    onSave,
    onRemove,
    onCancel
  } = t0;
  const {
    t
  } = useTranslation("common");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "collectDocument.acceptedDocuments"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t: tCountrySpecific
  } = useTranslation("playbook-builder", t1);
  const {
    control
  } = useFormContext();
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["country", "scope"];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== control) {
    t3 = {
      control,
      name: t2
    };
    $[2] = control;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const [countryDocMap, scope] = useWatch(t3);
  let t4;
  if ($[4] !== editingCountryDocMap) {
    t4 = editingCountryDocMap ? Object.keys(editingCountryDocMap)[0] : null;
    $[4] = editingCountryDocMap;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const editingCountry = t4;
  let t5;
  if ($[6] !== editingCountry) {
    t5 = COUNTRIES.find(country => country.value === editingCountry);
    $[6] = editingCountry;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const editingCountryRecord = t5;
  const editingDocs = editingCountryDocMap ? editingCountryDocMap[editingCountry as Iso3166TwoDigitCountryCode] as IdDocKind : null;
  let t6;
  if ($[8] !== countryDocMap || $[9] !== scope) {
    t6 = Object.keys(toDocumentTypesAndCountries({
      scope,
      country: countryDocMap
    }).countrySpecific);
    $[8] = countryDocMap;
    $[9] = scope;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const existingCountries = t6;
  let t7;
  if ($[11] !== editingCountry || $[12] !== existingCountries) {
    t7 = country_0 => country_0.value === editingCountry || !existingCountries.includes(country_0.value);
    $[11] = editingCountry;
    $[12] = existingCountries;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const countryOptions = COUNTRIES.filter(t7);
  const [selectedCountry, setSelectedCountry] = useState(editingCountryRecord ?? countryOptions[0]);
  const documentOptions = useIdDocOptions();
  let t8;
  if ($[14] !== documentOptions || $[15] !== editingCountry || $[16] !== editingDocs || $[17] !== selectedCountry.value) {
    t8 = selectedCountry.value === editingCountry ? documentOptions.filter(doc => editingDocs?.includes(doc.value)) : [];
    $[14] = documentOptions;
    $[15] = editingCountry;
    $[16] = editingDocs;
    $[17] = selectedCountry.value;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const preselectedDocs = t8;
  const {
    selectedDocuments,
    setSelectedDocuments
  } = useSelectedDocuments(preselectedDocs, selectedCountry);
  let t9;
  if ($[19] !== onSave || $[20] !== selectedCountry.value || $[21] !== selectedDocuments) {
    t9 = () => {
      onSave(selectedCountry.value, selectedDocuments.map(_temp));
    };
    $[19] = onSave;
    $[20] = selectedCountry.value;
    $[21] = selectedDocuments;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const handleSave = t9;
  let t10;
  if ($[23] !== selectedDocuments || $[24] !== setSelectedDocuments) {
    t10 = doc_0 => {
      const isSelected = selectedDocuments.some(selectedDoc => selectedDoc.value === doc_0.value);
      if (isSelected) {
        setSelectedDocuments(selectedDocuments.filter(d_0 => d_0.value !== doc_0.value));
      } else {
        setSelectedDocuments([...selectedDocuments, {
          ...doc_0
        }]);
      }
    };
    $[23] = selectedDocuments;
    $[24] = setSelectedDocuments;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const handleCheckboxChange = t10;
  const t11 = "flex flex-col rounded-md border border-solid border-tertiary";
  const t12 = "flex flex-col py-1";
  const t13 = "w-full pt-3 pb-3 pl-4 pr-4";
  const t14 = "[&>div]:w-full";
  const T0 = Select;
  const t15 = "Country";
  const t16 = selectedCountry.value;
  const t17 = value => {
    const country_2 = countryOptions.find(country_1 => country_1.value === value);
    if (country_2) {
      setSelectedCountry(country_2);
    }
  };
  const t18 = countryOptions.map(_temp2);
  let t19;
  if ($[26] !== T0 || $[27] !== selectedCountry.value || $[28] !== t17 || $[29] !== t18) {
    t19 = <div className={t13}><div className={t14}><T0 label={t15} value={t16} onChange={t17} options={t18} /></div></div>;
    $[26] = T0;
    $[27] = selectedCountry.value;
    $[28] = t17;
    $[29] = t18;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  if ($[31] !== documentOptions || $[32] !== handleCheckboxChange || $[33] !== selectedDocuments) {
    let t21;
    if ($[35] !== handleCheckboxChange || $[36] !== selectedDocuments) {
      t21 = doc_1 => {
        const isSelected_0 = selectedDocuments.some(selectedDoc_0 => selectedDoc_0.value === doc_1.value);
        return <CheckboxChip key={doc_1.value} isSelected={isSelected_0} onChange={() => handleCheckboxChange(doc_1)} label={doc_1.label} value={doc_1.value} />;
      };
      $[35] = handleCheckboxChange;
      $[36] = selectedDocuments;
      $[37] = t21;
    } else {
      t21 = $[37];
    }
    t20 = documentOptions.map(t21);
    $[31] = documentOptions;
    $[32] = handleCheckboxChange;
    $[33] = selectedDocuments;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  let t21;
  if ($[38] !== t20) {
    t21 = <div className="flex flex-wrap gap-2 pt-3 pb-4 px-4">{t20}</div>;
    $[38] = t20;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== scope || $[41] !== selectedDocuments.length || $[42] !== tCountrySpecific) {
    t22 = selectedDocuments.length === 0 && <div className="pb-4 px-4 text-body-3 text-tertiary">{scope === "specific_countries" ? tCountrySpecific(_temp3) : tCountrySpecific(_temp4)}</div>;
    $[40] = scope;
    $[41] = selectedDocuments.length;
    $[42] = tCountrySpecific;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] !== t19 || $[45] !== t21 || $[46] !== t22) {
    t23 = <div className={t12}>{t19}{t21}{t22}</div>;
    $[44] = t19;
    $[45] = t21;
    $[46] = t22;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <Divider variant="secondary" />;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  const t25 = scope === "specific_countries" && selectedDocuments.length === 0;
  let t26;
  if ($[49] !== t) {
    t26 = t(_temp5);
    $[49] = t;
    $[50] = t26;
  } else {
    t26 = $[50];
  }
  let t27;
  if ($[51] !== handleSave || $[52] !== t25 || $[53] !== t26) {
    t27 = <Button onClick={handleSave} variant="primary" disabled={t25}>{t26}</Button>;
    $[51] = handleSave;
    $[52] = t25;
    $[53] = t26;
    $[54] = t27;
  } else {
    t27 = $[54];
  }
  let t28;
  if ($[55] !== t) {
    t28 = t(_temp6);
    $[55] = t;
    $[56] = t28;
  } else {
    t28 = $[56];
  }
  let t29;
  if ($[57] !== onCancel || $[58] !== t28) {
    t29 = <Button onClick={onCancel} variant="secondary">{t28}</Button>;
    $[57] = onCancel;
    $[58] = t28;
    $[59] = t29;
  } else {
    t29 = $[59];
  }
  let t30;
  if ($[60] !== t27 || $[61] !== t29) {
    t30 = <div className="flex gap-2">{t27}{t29}</div>;
    $[60] = t27;
    $[61] = t29;
    $[62] = t30;
  } else {
    t30 = $[62];
  }
  let t31;
  if ($[63] !== onRemove || $[64] !== selectedCountry.value) {
    t31 = () => onRemove(selectedCountry.value);
    $[63] = onRemove;
    $[64] = selectedCountry.value;
    $[65] = t31;
  } else {
    t31 = $[65];
  }
  let t32;
  if ($[66] !== t) {
    t32 = t(_temp7);
    $[66] = t;
    $[67] = t32;
  } else {
    t32 = $[67];
  }
  let t33;
  if ($[68] !== t31 || $[69] !== t32) {
    t33 = <LinkButton iconComponent={IcoTrash16} iconPosition="left" onClick={t31} destructive={true}>{t32}</LinkButton>;
    $[68] = t31;
    $[69] = t32;
    $[70] = t33;
  } else {
    t33 = $[70];
  }
  let t34;
  if ($[71] !== t30 || $[72] !== t33) {
    t34 = <div className="flex items-center justify-between py-3 px-4">{t30}{t33}</div>;
    $[71] = t30;
    $[72] = t33;
    $[73] = t34;
  } else {
    t34 = $[73];
  }
  let t35;
  if ($[74] !== t23 || $[75] !== t34) {
    t35 = <div className={t11}>{t23}{t24}{t34}</div>;
    $[74] = t23;
    $[75] = t34;
    $[76] = t35;
  } else {
    t35 = $[76];
  }
  return t35;
};
export default Picker;
function _temp(d) {
  return d.value;
}
function _temp2(country_3) {
  return {
    label: country_3.label,
    value: country_3.value
  };
}
function _temp3($) {
  return $.selectDocumentHint;
}
function _temp4($_0) {
  return $_0.noDocumentsHint;
}
function _temp5($_1) {
  return $_1.save;
}
function _temp6($_2) {
  return $_2.cancel;
}
function _temp7($_3) {
  return $_3.remove;
}